/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { convertForDataTest, ConvertForDataTestPipe, IsNotNullPipe } from '@common/tech-shared';

import { NgClass } from '@angular/common';
import { Component, computed, input, Signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { cva, VariantProps } from 'class-variance-authority';
import { BadgeComponent, BadgeConfig } from '../../badge/badge.component';
import { IconComponent, IconConfig } from '../../icon/icon.component';
import { ButtonIconName } from '../../icon/icons';

export const navItemVariants = cva(
  'focus-visible:outline-focus hover:border-primary focus-visible:border-background-200 flex min-h-8 items-center justify-between rounded-2xl border border-transparent px-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
  {
    variants: {
      size: {
        small: 'py-1.5',
        medium: 'py-2',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
);
export type NavItemVariants = VariantProps<typeof navItemVariants>;

@Component({
  selector: 'ods-nav-item',
  imports: [NgClass, RouterLink, RouterLinkActive, ConvertForDataTestPipe, BadgeComponent, IsNotNullPipe, IconComponent],
  host: { role: 'menuitem' },
  template: `<a
    [routerLink]="path()"
    [routerLinkActive]="routerActiveClass()"
    [ngClass]="[navItemVariants({ size: size() }), selected() ? 'selected bg-selected-light [&]:border-selected' : '']"
    [attr.data-test-id]="itemTestId()"
  >
    <div class="flex items-center gap-2">
      <ng-content />
      @if (icon(); as iconName) {
        <ods-icon [name]="iconName" [config]="iconConfig()" />
      }
      <p class="text-text text-left text-sm" [attr.data-test-id]="'caption-' + caption() | convertForDataTest">{{ caption() }}</p>
    </div>
    @if (count() | isNotNull) {
      <span class="text-text text-sm" [attr.data-test-id]="itemTestId() + '-count'">{{ count() }}</span>
    }
    @if (badge(); as badgeConfig) {
      <ods-badge data-test-class="nav-item-badge" [name]="badgeConfig.text" [color]="badgeConfig.color" />
    }
  </a>`,
})
export class NavItemComponent {
  public readonly caption = input.required<string>();
  public readonly path = input<string>();
  public readonly badge = input<BadgeConfig>();
  public readonly selected = input(false);
  public readonly size = input<NavItemVariants['size']>();
  public readonly count = input<number>();
  public readonly dataTestId = input<string>();
  public readonly routeActive = input(true);
  public readonly icon = input<ButtonIconName>();
  public readonly iconConfig = input<IconConfig>({ fill: 'text' });

  public readonly navItemVariants = navItemVariants;

  public readonly itemTestId: Signal<string> = computed(
    () => this.dataTestId() || convertForDataTest('link-path-' + this.path()),
  );

  public readonly routerActiveClass: Signal<string> = computed(() =>
    this.routeActive() ? 'bg-selected-light border-selected' : '',
  );
}
